import {
  FeaturesNames,
  PackageOfServices,
  PermissionGroup,
  ServicesList,
} from '@/shared/types/settings'

export type FeatureAccessOverride = 'api' | 'on' | 'off' | 'package' | 'group'

export const DEV_GROUP_PREFIX = 'dev-grp-'
export const DEV_PACKAGE_GUID = 'dev-pkg-emulator'
export const DEV_PACKAGE_NAME = 'DevTools'

export const FEATURE_PROVISION_MAP = {
  instantMessaging: FeaturesNames.INSTANT_MESSAGING,
  cdrsAccess: FeaturesNames.CDRS_ACCESS,
  webCallBackorder: FeaturesNames.WEB_CALL_BACKORDER,
  webToFax: FeaturesNames.WEB_TO_FAX,
  forward: FeaturesNames.FORWARD,
  blackWhiteList: FeaturesNames.BLACK_WHITE_LIST,
  voiceMailBoxAccess: FeaturesNames.VOICE_MAILBOX_ACCESS,
  accessGroupManagement: FeaturesNames.ACCESS_GROUP_MANAGEMENT,
  doNotDisturb: FeaturesNames.DO_NOT_DISTURB,
} as const

export type FeatureProvisionField = keyof typeof FEATURE_PROVISION_MAP

export const isProvisionOverride = (
  value: FeatureAccessOverride | undefined,
): value is 'package' | 'group' | 'off' =>
  value === 'package' || value === 'group' || value === 'off'

export const guidForFeature = (name: FeaturesNames) => `${DEV_GROUP_PREFIX}${name}`

export const featureFromGuid = (guid: string): FeaturesNames | undefined => {
  if (!guid.startsWith(DEV_GROUP_PREFIX)) return undefined
  return guid.slice(DEV_GROUP_PREFIX.length) as FeaturesNames
}

const createService = (name: FeaturesNames): ServicesList => ({
  guid: `dev-svc-${name}`,
  id: `dev-svc-${name}`,
  name,
  isDefault: false,
  dstResult: '0',
  isUserServices: false,
})

const createGroup = (name: FeaturesNames, aclEnabled: boolean): PermissionGroup => ({
  guid: guidForFeature(name),
  name,
  type: name === FeaturesNames.ACCESS_GROUP_MANAGEMENT ? 'access' : 'feature',
  aclEnabled,
})

const createPackage = (groupNames: string[]): PackageOfServices => ({
  guid: DEV_PACKAGE_GUID,
  enabled: true,
  name: DEV_PACKAGE_NAME as FeaturesNames,
  isDefault: false,
  licensingOption: '',
  price: 0,
  title: DEV_PACKAGE_NAME,
  description: '',
  groupNames: groupNames as [string],
})

export const overlayPermissionGroups = (
  groups: PermissionGroup[],
  provisions: Partial<Record<FeaturesNames, FeatureAccessOverride>>,
): PermissionGroup[] => {
  const controlled = new Set(Object.keys(provisions) as FeaturesNames[])
  const next = groups.filter((item) => !controlled.has(item.name))

  controlled.forEach((name) => {
    const override = provisions[name]
    if (override === 'package') return
    if (override === 'group') next.push(createGroup(name, true))
    if (override === 'off') next.push(createGroup(name, false))
  })

  return next
}

export const overlayServices = (
  services: ServicesList[],
  provisions: Partial<Record<FeaturesNames, FeatureAccessOverride>>,
): ServicesList[] => {
  const controlled = new Set(Object.keys(provisions) as FeaturesNames[])
  const next = services.filter((item) => !controlled.has(item.name))

  controlled.forEach((name) => {
    const override = provisions[name]
    if (override === 'package' || override === 'group') next.push(createService(name))
  })

  return next
}

export const overlayPackages = (
  packages: PackageOfServices[],
  provisions: Partial<Record<FeaturesNames, FeatureAccessOverride>>,
): PackageOfServices[] => {
  const controlled = new Set(Object.keys(provisions) as FeaturesNames[])
  const packageNames = (Object.keys(provisions) as FeaturesNames[]).filter((name) => provisions[name] === 'package')

  const next = packages
    .filter((item) => item.guid !== DEV_PACKAGE_GUID)
    .map((item) => ({
      ...item,
      groupNames: item.groupNames.filter((name) => !controlled.has(name as FeaturesNames)) as [string],
    }))

  if (packageNames.length > 0) next.push(createPackage(packageNames))

  return next
}
